import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { safeFetch, safeFetchSingle, safeFetchCount } from '@/lib/safeFetch';

export interface Church {
  id: string;
  name: string;
  address?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string; // Legacy field name, maps to postal_code in DB
  postal_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  logo_url?: string;
  plan_tier?: string;
  status?: string;
  created_at: string;
  updated_at: string;
  member_count?: number;
  // Additional columns present on the churches table (see Supabase types).
  subdomain?: string | null;
  plan?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  sendgrid_verified?: boolean | null;
  a2p_verified?: boolean | null;
  timezone?: string | null;
  member_limit?: number | null;
  active_member_count?: number | null;
  email_credits?: number | null;
  sms_credits?: number | null;
}

export interface CreateChurchData {
  name: string;
  subdomain: string;
  timezone: string;
  primary_contact_email: string;
  primary_contact_phone: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  plan: string;
  member_limit: number;
  monthly_email_limit: number;
  monthly_sms_limit: number;
  sendgrid_sender_id: string;
  twilio_service_id: string;
  admin_user: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

// Church admin actions: suspend/activate (status updates) and create (via the
// super-admin-gated create-church edge function). Replaces earlier non-functional
// stubs in the admin pages.
export const useChurchActions = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['churches'] });

  const setStatus = (status: string) => async (churchId: string) => {
    // .select() so we can detect a 0-row update (church not found, or RLS blocked
    // the write) and fail loudly instead of reporting a false success.
    const { data, error } = await supabase
      .from('churches')
      .update({ status })
      .eq('id', churchId)
      .select('id');
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('Could not update church status (not found or insufficient permissions).');
    }
  };

  const suspendChurch = useMutation({
    mutationFn: setStatus('suspended'),
    onSuccess: invalidate,
  });

  const activateChurch = useMutation({
    mutationFn: setStatus('active'),
    onSuccess: invalidate,
  });

  const createChurch = useMutation({
    mutationFn: async (data: CreateChurchData) => {
      const { data: result, error } = await supabase.functions.invoke('create-church', {
        body: data,
      });
      if (error) throw error;
      return result;
    },
    onSuccess: invalidate,
  });

  return { suspendChurch, activateChurch, createChurch };
};

// Default church data to prevent undefined access
const DEFAULT_CHURCHES: Church[] = [];

export const useChurches = () => {
  console.log('🔍 useChurches - Hook called');
  
  const queryResult = useQuery({
    queryKey: ['churches'],
    queryFn: async (): Promise<Church[]> => {
      console.log('🔍 useChurches - QueryFn EXECUTING NOW');
      try {
        console.log('🔍 useChurches - Starting query...');
        
        // Check if user is authenticated
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session) {
          console.warn('⚠️ useChurches - No session found:', sessionError);
          return DEFAULT_CHURCHES;
        }
        
        console.log('✅ useChurches - User authenticated:', session.user.email);
        
        // Check if user has super_admin role
        const { data: userRoles, error: roleError } = await supabase
          .from('user_church_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'super_admin')
          .limit(1);
        
        const isSuperAdmin = !roleError && userRoles && userRoles.length > 0;
        console.log('🔍 useChurches - User role check:', { isSuperAdmin, roleError: roleError?.message });
        
        // Query directly to see actual errors
        console.log('🔍 useChurches - About to query churches table...');
        const { data, error } = await supabase
          .from('churches')
          .select(`
            id,
            name,
            address1,
            address2,
            city,
            state,
            postal_code,
            primary_contact_phone,
            primary_contact_email,
            website_url,
            logo_url,
            plan,
            status,
            created_at,
            updated_at
          `)
          .order('name', { ascending: true });

        console.log('🔍 useChurches - Query completed:', { hasData: !!data, dataLength: data?.length, hasError: !!error });

        if (error) {
          console.error('🚨 useChurches - Supabase error:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code,
            isSuperAdmin
          });
          // For superadmins, this might be an RLS issue - log it prominently
          if (error.code === 'PGRST301' || error.message?.includes('permission') || error.message?.includes('policy')) {
            console.error('🚨 useChurches - RLS Policy Error - Superadmins should have access to all churches!');
            console.error('🚨 useChurches - This might be an RLS policy configuration issue.');
          }
          return DEFAULT_CHURCHES;
        }

        if (!Array.isArray(data) || data.length === 0) {
          console.warn('⚠️ useChurches - No churches returned from query', { isSuperAdmin, data });
          return DEFAULT_CHURCHES;
        }

        console.log(`✅ useChurches - Found ${data.length} churches`);

        // Get member counts for each church safely
        const churchesWithCounts = await Promise.all(
          data.map(async (church: any) => {
            const memberCount = await safeFetchCount(
              supabase
                .from('user_church_roles')
                .select('*', { count: 'exact', head: true })
                .eq('church_id', church.id)
            );

            // Create address field from address1/address2 for backward compatibility
            const address = [church.address1, church.address2].filter(Boolean).join(', ');

            return {
              ...church,
              address,
              // Map database columns to interface fields
              phone: church.primary_contact_phone,
              email: church.primary_contact_email,
              website: church.website_url,
              plan_tier: church.plan,
              zip_code: church.postal_code || church.zip_code, // Map postal_code to zip_code for backward compatibility
              postal_code: church.postal_code || church.zip_code,
              member_count: memberCount || 0,
              status: church.status || 'active'
            };
          })
        );

        return churchesWithCounts;
      } catch (err) {
        console.error('🚨 useChurches error:', err);
        console.error('🚨 useChurches error stack:', err instanceof Error ? err.stack : 'No stack');
        return DEFAULT_CHURCHES;
      }
    },
    // Remove initialData to force query to run
    // initialData: DEFAULT_CHURCHES,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
  
  console.log('🔍 useChurches - Query result:', {
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    dataLength: queryResult.data?.length || 0,
    isFetching: queryResult.isFetching
  });
  
  return queryResult;
};

export const useChurch = (churchId: string) => {
  return useQuery({
    queryKey: ['church', churchId],
    queryFn: async (): Promise<Church | null> => {
      try {
        if (!churchId) return null;

        console.log('🔍 useChurch - Fetching church data for:', churchId);

        // Try direct query first with better error handling
        const { data, error } = await supabase
          .from('churches')
          .select(`
            id,
            name,
            address1,
            address2,
            city,
            state,
            postal_code,
            primary_contact_phone,
            primary_contact_email,
            website_url,
            logo_url,
            plan,
            status,
            created_at,
            updated_at
          `)
          .eq('id', churchId)
          .single();

        if (error) {
          console.error('🚨 useChurch - Direct Supabase error:', {
            error: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code,
            churchId
          });
          // Return mock data to prevent UI from breaking
          return {
            id: churchId,
            name: 'Church Loading...',
            address: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip_code: '',
            postal_code: '',
            phone: '',
            email: '',
            website: '',
            logo_url: '',
            plan_tier: 'basic',
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            member_count: 0
          };
        }

        if (!data) {
          console.warn('⚠️ useChurch - No data returned for church:', churchId);
          return null;
        }

        console.log('✅ useChurch - Successfully fetched church data:', data.name);

        // Get member count safely - don't let this block the church data
        let memberCount = 0;
        try {
          memberCount = await safeFetchCount(
            supabase
              .from('user_church_roles')
              .select('*', { count: 'exact', head: true })
              .eq('church_id', churchId)
          );
        } catch (memberErr) {
          console.warn('⚠️ useChurch - Could not fetch member count:', memberErr);
        }

        // Create address field from address1/address2 for backward compatibility
        const address = [data.address1, data.address2].filter(Boolean).join(', ');

        return {
          ...data,
          address,
          // Map database columns to interface fields
          phone: data.primary_contact_phone,
          email: data.primary_contact_email,
          website: data.website_url,
          plan_tier: data.plan,
          zip_code: data.postal_code || data.zip_code, // Map postal_code to zip_code for backward compatibility
          postal_code: data.postal_code || data.zip_code,
          member_count: memberCount || 0,
          status: data.status || 'active'
        };
      } catch (err) {
        console.error('🚨 useChurch - Unexpected error:', err);
        // Return mock data to prevent complete UI failure
        return {
          id: churchId,
          name: 'Church (Error Loading)',
          address: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip_code: '',
          phone: '',
          email: '',
          website: '',
          logo_url: '',
          plan_tier: 'basic',
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          member_count: 0
        };
      }
    },
    enabled: !!churchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once to avoid infinite loops
  });
};